import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetInvestigationRead } from '@/application/services/investigation.service'
import { useJsonLd } from '@/application/hooks/useJsonLd'
import { usePageLangAndHreflang, summarize } from '@/application/hooks/useDocumentMeta'
import { absoluteImageUrl, PUBLISHER_LD_ID, PUBLISHER_NAME } from '@/application/seo/site'
import { useLocalizedUrl } from '@/application/hooks/useLocalizedUrl'
import { resolveLanguage, SUPPORTED_LANG_CODES } from '@/application/i18n/language'
import type { InvestigationSlugs } from '@/domains/types'
import { InvestigationNotFound } from './InvestigationNotFound'
import { ReadModeSkeleton } from './ReadModeSkeleton'
import { ReadArticleHeader } from './ReadArticleHeader'
import { ReadTableOfContents } from './ReadTableOfContents'
import { ReadLeadSection } from './ReadLeadSection'
import { ArticleCommentsSection } from './ArticleCommentsSection'
import { PreviewModeBanner } from './PreviewModeBanner'
import InvestigationLiveBanner from '../InvestigationLiveBanner'

/**
 * The public investigation detail page's only view: fetches the compiled
 * `InvestigationRead` DTO in one request and composes the toolbar, masthead,
 * table of contents, lead sections and comments into a single
 * scrollable/printable article. Owns its own loading/not-found handling.
 */
export function InvestigationReadView({
  id,
  slugs,
}: {
  id: string
  /** Current slug per language: the URL of each language of this article. */
  slugs?: InvestigationSlugs
}) {
  const { i18n } = useTranslation()
  const [showingPublicOnly, setShowingPublicOnly] = useState(false)
  const { data, isLoading } = useGetInvestigationRead(id, showingPublicOnly)

  const locale = resolveLanguage(i18n.language)
  const pathsByLocale = Object.fromEntries(
    SUPPORTED_LANG_CODES.flatMap((code) => {
      const slug = slugs?.[code]
      return slug ? [[code, `/investigations/${slug}`]] : []
    }),
  )
  const pagePath = pathsByLocale[locale] ?? `/investigations/${id}`
  const pageUrl = useLocalizedUrl(pagePath)

  // `availableLocales` comes straight from the API's compiled response
  // (`TranslationAvailabilityService`, TRANSLATION-PLAN.md §6/§7) — never
  // recomputed here. That's what keeps this emitter and the backend's
  // sitemap/share-page emitters in agreement (Invariant 1).
  usePageLangAndHreflang(data ? pagePath : null, data?.availableLocales ?? ['fr'], pathsByLocale)

  useJsonLd(
    'ld-json-article',
    data
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: data.investigation.name,
          description: summarize(data.investigation.description),
          datePublished: data.investigation.createdAt,
          dateModified: data.investigation.updatedAt,
          image: absoluteImageUrl(data.investigation.imageUrl),
          url: pageUrl,
          author: { '@type': 'Person', '@id': PUBLISHER_LD_ID, name: PUBLISHER_NAME },
          publisher: { '@id': PUBLISHER_LD_ID },
          mainEntityOfPage: pageUrl,
        }
      : null,
  )

  if (isLoading) {
    return <ReadModeSkeleton />
  }

  if (!data) {
    return <InvestigationNotFound />
  }

  const { investigation, leads } = data

  // Only a privileged viewer's own session can put unpublished content in
  // this response (InvestigationReadService.compile's proof-read branch) —
  // an anonymous visitor never receives an unpublished lead/fact here, so
  // this is a reliable signal to show the preview toggle.
  const hasUnpublishedContent =
    !showingPublicOnly &&
    leads.some((entry) => !entry.lead.published || entry.facts.some((fact) => !fact.published))

  // `lang` is what lets the browser pick a hyphenation dictionary for the
  // justified article text; `index.html` carries a static `lang="en"`, so
  // without it the French/Spanish UIs would be hyphenated with English rules.
  // It is also the right pronunciation hint for screen readers.
  return (
    <article
      lang={i18n.language}
      className="read-article mx-auto grow w-full max-w-[100rem] bg-card px-5 py-6 sm:px-8 sm:py-8 2xl:border-x 2xl:border-border"
    >
      {(hasUnpublishedContent || showingPublicOnly) && (
        <PreviewModeBanner
          showingPublicOnly={showingPublicOnly}
          onToggle={() => setShowingPublicOnly((previous) => !previous)}
        />
      )}
      <div className="mb-5 empty:hidden">
        <InvestigationLiveBanner investigation={investigation} />
      </div>
      <ReadArticleHeader
        investigation={investigation}
        leads={leads}
        pageUrl={pageUrl}
        availableLocales={data.availableLocales}
      />
      <ReadTableOfContents leads={leads.map((entry) => entry.lead)} />
      {leads.map((entry) => (
        <ReadLeadSection key={entry.lead.id} entry={entry} />
      ))}
      <ArticleCommentsSection investigationId={id} />
    </article>
  )
}
